import React from 'react';
import { useDispatch } from 'react-redux';
import { Formik } from 'formik';
import * as yup from 'yup';
import NumberFormat from 'react-number-format';
import {
  Button,
  Checkbox,
  FormControlLabel,
  Icon,
  IconButton,
  MenuItem,
  TextField,
  Typography
} from '@material-ui/core';
import { updateCustomerDataApi } from 'api';
import {states} from 'helpers/usStates';
import { addCountryCode } from 'helpers/other';


const CustomerAddressForm = (
  {customer, editableAddress, toggleAddressForm, setEditableAddress}
) => {
  const dispatch = useDispatch();

  const handleSubmitAddress = (values) => {
    const updatedAddressList = editableAddress?.id
      ? customer.addresses.filter(({ id }) => id !== editableAddress.id)
      : customer.addresses;

    const editedAddressValues = editableAddress?.id
      ? {...values, id: editableAddress.id}
      : values;
    const editableCustomer = {
      ...customer,
      addresses: [
        ...updatedAddressList,
        editedAddressValues
      ]
    };

    updateCustomerDataApi({
      dispatch,
      customerId: customer.id,
      customerData: editableCustomer
    });
    setEditableAddress({});
    toggleAddressForm(false);
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-5">
        <Typography variant="h5">
          {`${editableAddress?.id ? 'Edit' : 'Add New'} Customer Address`}
        </Typography>
        <IconButton
          onClick={() => toggleAddressForm(false)}
        >
          <Icon>close</Icon>
        </IconButton>
      </div>
      <Formik
        initialValues={{
          firstname: editableAddress?.firstname || '',
          lastname: editableAddress?.lastname || '',
          street: editableAddress?.street || ['', '', ''],
          city: editableAddress?.city || '',
          country_id: 'US',
          region: {
            region_id: editableAddress?.region?.region_id || '',
            region_code: editableAddress?.region?.region_code || '',
            region: editableAddress?.region?.region || '',
          },
          region_id: editableAddress?.region_id || '',
          postcode: editableAddress?.postcode || '',
          telephone: addCountryCode(editableAddress?.telephone || ''),
          default_billing: editableAddress?.default_billing || false,
          default_shipping: editableAddress?.default_shipping || false,
        }}
        onSubmit={(values) => handleSubmitAddress(values)}
        validationSchema={yup.object({
          firstname: yup
            .string()
            .required('Field is required'),
          lastname: yup
            .string()
            .required('Field is required'),
          street: yup
            .array()
            .of(
              yup.string()
            )
            .test(
              'street',
              'Field is required',
              (val) =>
                val.every(
                  (val, index) => {
                    if (index === 0) {
                      return !!val;
                    }

                    return true;
                  }
                )
            ),
          city: yup
            .string()
            .required('Field is required'),
          region_id: yup
            .string()
            .required('Field is required'),
          postcode: yup
            .string()
            .matches(/(^\d{5}$)|(^\d{5}-\d{4}$)/, '"Please enter a valid zip code (Ex: 90602 or 90602-1234)"')
            .required('Field is required'),
          telephone: yup
            .string()
            .test('len', 'Enter valid phone number', (val) => val?.toString().length >= 11)
            .required('Field is required')
        })}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          setFieldValue
        }) => (
          <form noValidate onSubmit={handleSubmit}>
            <TextField
              required
              fullWidth
              className="mb-4"
              variant="outlined"
              name="firstname"
              label="First Name"
              value={values.firstname}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched?.firstname && Boolean(errors?.firstname)}
              helperText={touched?.firstname && errors?.firstname}
            />
            <TextField
              required
              fullWidth
              className="mb-4"
              variant="outlined"
              name="lastname"
              label="Last Name"
              value={values.lastname}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched?.lastname && Boolean(errors?.lastname)}
              helperText={touched?.lastname && errors?.lastname}
            />
            <TextField
              required
              fullWidth
              className="mb-4"
              variant="outlined"
              name="street[0]"
              label="Street Address 1"
              value={values.street[0]}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched?.street?.[0] && Boolean(errors?.street?.[0])}
              helperText={touched?.street?.[0] && errors?.street}
            />
            <TextField
              fullWidth
              className="mb-4"
              variant="outlined"
              name="street[1]"
              label="Street Address 2"
              value={values.street[1]}
              onChange={handleChange}
              onBlur={handleBlur}
            />
            <TextField
              fullWidth
              className="mb-4"
              variant="outlined"
              name="street[2]"
              label="Street Address 3"
              value={values.street[2]}
              onChange={handleChange}
              onBlur={handleBlur}
            />
            <TextField
              required
              fullWidth
              className="mb-4"
              variant="outlined"
              name="city"
              label="City"
              value={values.city}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched?.city && Boolean(errors?.city)}
              helperText={touched?.city && errors?.city}
            />
            <TextField
              required
              fullWidth
              select
              className="mb-4"
              variant="outlined"
              name="region_id"
              label="State"
              value={values.region.region_id}
              onChange={({target: {value}}) => {
                const {value: code, name, shortName} = states.find((state) => state.value == value);
                setFieldValue('region_id', code);
                setFieldValue('region.region_id', code);
                setFieldValue('region.region_code', shortName);
                setFieldValue('region.region', name);
              }}
              error={touched?.region_id && Boolean(errors?.region_id)}
              helperText={touched?.region_id && errors?.region_id}
            >
              {states.map((state) => (
                <MenuItem value={Number(state.value)} key={Number(state.value)}>
                  {state.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              required
              fullWidth
              className="mb-4"
              variant="outlined"
              name="postcode"
              label="Zip/Postal Code"
              value={values.postcode}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched?.postcode && Boolean(errors?.postcode)}
              helperText={touched?.postcode && errors?.postcode}
            />
            <NumberFormat
              customInput={TextField}
              format="+# (###)-###-####"
              mask="_"
              required
              className="w-full"
              label="Phone"
              variant="outlined"
              name="telephone"
              value={values.telephone}
              onValueChange={(value) => {
                setFieldValue('telephone', String(value.floatValue));
              }}
              onBlur={handleBlur}
              error={touched?.telephone && Boolean(errors?.telephone)}
              helperText={touched?.telephone && errors?.telephone}
            />
            <FormControlLabel
              control={
                <Checkbox
                  color="primary"
                  size="small"
                  checked={values.default_billing}
                  onChange={({target: {checked}}) => setFieldValue('default_billing', checked)}
                />
              }
              label="Default Billing Address"
            />
            <FormControlLabel
              control={
                <Checkbox
                  color="primary"
                  size="small"
                  checked={values.default_shipping}
                  onChange={({target: {checked}}) => setFieldValue('default_shipping', checked)}
                />
              }
              label="Default Shipping Address"
            />
            <div className="flex items-center justify-between mt-5">
              <Button
                color="primary"
                variant="outlined"
                onClick={() => {
                  toggleAddressForm(false);
                  setEditableAddress({});
                }}
              >
                Cancel
              </Button>
              <Button
                color="primary"
                variant="contained"
                type="submit"
              >
                Submit
              </Button>
            </div>
          </form>
        )}
      </Formik>
    </div>
  );
};

export default CustomerAddressForm;
